import { apiService } from "@/services/apiService";
import { storage } from "@/services/storage";
import type { AIAVDataDto } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { FileUpload } from "@repo/ui/components/Base/FileUpload";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { Input } from "@repo/ui/components/Base/Input";
import { RadioInput } from "@repo/ui/components/Base/RadioInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { PencilIcon, PlusIcon, StarsIcon } from "lucide-react";
import { useState } from "react";
import shortUUID from "short-uuid";
import { MeetingMinutesAITemplateDialog } from "./MeetingMinutesAITemplateDialog";
import { useRefresh } from "@repo/ui/components/Base/hooks/useRefresh";
import { wait } from "@repo/lib/wait";
import { useAIMinutesTemplateList } from "@/hooks/queryHooks/aiMinutesTemplateHooks";
import {
  aiMinutesTemplateRepo,
  type AIMinutesTemplateDto,
} from "@/repositories/aiMinutesTemplateRepo";
import { extractExtension } from "@repo/lib/extractExtension";

export interface MeetingMinutesAIDialogProps {
  tenantId: string;
  meetingId: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  onEnhanced?: (enhancedMinutes: string) => void;
}

export const MeetingMinutesAIDialog = ({
  tenantId,
  meetingId,
  open,
  setOpen,
  onEnhanced,
}: MeetingMinutesAIDialogProps) => {
  const [useRecording, setUseRecording] = useState(false);
  const [recordingType, setRecordingType] = useState<"file" | "url">("file");
  const [summaryLevel, setSummaryLevel] = useState<"none" | "medium" | "high">(
    "none"
  );
  const [recordingUrl, setRecordingUrl] = useState("");
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [useFormatting, setUseFormatting] = useState(false);
  const [formattingTemplate, setFormattingTemplate] = useState("");
  const { data: aiMinutesTemplates } = useAIMinutesTemplateList(tenantId);
  const refresh = useRefresh();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AIMinutesTemplateDto | null>(null);

  const handleSubmit = useFutureCallback(async () => {
    setError("");
    setLoading(true);
    setStatus("Starting...");

    try {
      let path = "";
      if (useRecording) {
        if (recordingType === "file" ? !recordingFile : !recordingUrl) {
          setError(
            recordingType === "file"
              ? "Please select a recording file."
              : "Please enter a recording URL."
          );
          return;
        }
        if (recordingType === "file" && recordingFile) {
          const ext = extractExtension(recordingFile.name);
          path = `${tenantId}/meetingRecordings/${meetingId}/${shortUUID.generate()}${ext ? "." + ext : ""}`;
          setStatus("Uploading recording...");
          await storage.upload(recordingFile, path);
        }
      }

      const audioFile: AIAVDataDto =
        recordingType === "file"
          ? { type: "upload", path }
          : { type: "url", url: recordingUrl };

      const minutesTemplateId = useFormatting ? formattingTemplate : undefined;

      let jobId: string | undefined;

      setStatus("Queuing job...");

      if (useRecording) {
        jobId = (
          await apiService.v1.generateMinutesWithAV(tenantId, {
            audioFile,
            meetingId,
            minutesTemplateId,
            summarizationLevel: summaryLevel,
          })
        ).data?.jobId;
      } else {
        jobId = (
          await apiService.v1.generateMinutes(tenantId, {
            meetingId,
            minutesTemplateId,
          })
        ).data?.jobId;
      }

      if (!jobId) {
        setError("Failed to queue job. Check your inputs.");
        return;
      }

      let processing = true;
      while (processing) {
        await wait(1000);

        const status = (await apiService.v1.getJobStatus(tenantId, jobId)).data;
        if (!status) {
          processing = false;
          break;
        }

        switch (status.status) {
          case "complete":
            processing = false;
            break;
          case "queued":
            setStatus(`Job waiting in queue... ${status.progress}%`);
            break;
          case "transcribing":
            setStatus(`Transcribing audio recording... ${status.progress}%`);
            break;
          case "generating_minutes":
            setStatus(`Generating meeting minutes... ${status.progress}%`);
            break;
        }
      }

      setStatus("Job finished. Downloading result...");

      const result = (await apiService.v1.getJobResult(tenantId, jobId)).data;
      if (!result) {
        setError("Failed to get response. Job may have been cancelled.");
        return;
      }
      if (!result.minutes) {
        setError(
          `An error occurred while processing: ${result.error ?? "<Unknown>"}`
        );
        return;
      }

      onEnhanced?.(result.minutes);
    } finally {
      setLoading(false);
      setStatus("");
    }
  });

  return (
    <>
      {selectedTemplate && (
        <MeetingMinutesAITemplateDialog
          open={templateDialogOpen}
          setOpen={setTemplateDialogOpen}
          aiMinutesTemplate={selectedTemplate}
          showDelete={!!selectedTemplate.id}
          onSubmit={async (name, delta) => {
            const data = JSON.stringify(delta);

            if (!selectedTemplate.id) {
              const newTemplate = await aiMinutesTemplateRepo.create(tenantId, {
                name,
                data,
              });
              if (newTemplate) {
                aiMinutesTemplates?.push(newTemplate);
                setFormattingTemplate(newTemplate.id);
              }
            } else {
              await aiMinutesTemplateRepo.update(tenantId, formattingTemplate, {
                name,
                data,
              });
              selectedTemplate.name = name;
              selectedTemplate.data = data;
            }

            refresh();
          }}
          onDelete={async (template) => {
            if (template.id) {
              await aiMinutesTemplateRepo.delete(tenantId, template.id);
              if (aiMinutesTemplates) {
                const index = aiMinutesTemplates.indexOf(template);
                if (index > -1) aiMinutesTemplates.splice(index, 1);
              }
              setSelectedTemplate(null);
              setFormattingTemplate("");
              refresh();
            }
          }}
        />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent preventClickout preventEscape className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enhance Meeting Minutes With AI</DialogTitle>
          </DialogHeader>

          <DialogDescription>
            AI meeting minutes assistant options
          </DialogDescription>

          <DialogBody>
            <div className="flex flex-col gap-5">
              <CheckboxInput
                disabled={loading}
                label="Use Recording"
                orientation="horizontal"
                checked={useRecording}
                onCheckedChange={(checked) => setUseRecording(!!checked)}
              />

              {useRecording && (
                <div className="ml-8 flex flex-col gap-5">
                  <div className="flex flex-row gap-2 items-center">
                    <RadioInput
                      disabled={loading}
                      label="File"
                      orientation="horizontal"
                      containerStyle="grow-[1] flex-row-reverse w-0 mt-0"
                      labelStyle="w-full [&+div]:w-auto"
                      checked={recordingType === "file"}
                      onCheckedChange={(checked) => {
                        if (checked) setRecordingType("file");
                      }}
                    />

                    <FileUpload
                      disabled={recordingType !== "file" || loading}
                      small
                      className="grow-[4] w-0"
                      accept="all"
                      dropTargetMessage={recordingFile?.name}
                      handleFileUpload={(file) => {
                        setRecordingFile(file);
                      }}
                    />
                  </div>

                  <div className="flex flex-row gap-2 items-start">
                    <RadioInput
                      disabled={loading}
                      label="URL"
                      orientation="horizontal"
                      containerStyle="grow-[1] flex-row-reverse w-0 mt-4"
                      labelStyle="w-full [&+div]:w-auto"
                      checked={recordingType === "url"}
                      onCheckedChange={(checked) => {
                        if (checked) setRecordingType("url");
                      }}
                    />

                    <div className="flex flex-col gap-2 grow-[4] w-0 mt-0">
                      <Input
                        id="recording-url"
                        disabled={recordingType !== "url" || loading}
                        containerStyle="w-full"
                        value={recordingUrl}
                        onChange={(evt) => setRecordingUrl(evt.target.value)}
                      />
                      <p className="italic text-sm">
                        Note: YouTube urls may not always work.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <p className="font-bold">Summarization Level</p>

                    <RadioInput
                      disabled={loading}
                      containerStyle="flex-row-reverse mt-0"
                      labelStyle="w-full [&+div]:w-auto"
                      label="None (full transcript)"
                      orientation="horizontal"
                      checked={summaryLevel === "none"}
                      onCheckedChange={(checked) => {
                        if (checked) setSummaryLevel("none");
                      }}
                    />

                    <RadioInput
                      disabled={loading}
                      containerStyle="flex-row-reverse mt-0"
                      labelStyle="w-full [&+div]:w-auto"
                      label="Medium (some summarization)"
                      orientation="horizontal"
                      checked={summaryLevel === "medium"}
                      onCheckedChange={(checked) => {
                        if (checked) setSummaryLevel("medium");
                      }}
                    />

                    <RadioInput
                      disabled={loading}
                      containerStyle="flex-row-reverse mt-0"
                      labelStyle="w-full [&+div]:w-auto"
                      label="High (summarized discussion)"
                      orientation="horizontal"
                      checked={summaryLevel === "high"}
                      onCheckedChange={(checked) => {
                        if (checked) setSummaryLevel("high");
                      }}
                    />
                  </div>
                </div>
              )}

              <CheckboxInput
                disabled={loading}
                label="Use Formatting"
                orientation="horizontal"
                checked={useFormatting}
                onCheckedChange={(checked) => setUseFormatting(!!checked)}
              />

              {useFormatting && (
                <div className="ml-8">
                  <div className="flex flex-row gap-2 items-center">
                    <SelectInput
                      disabled={loading}
                      containerStyle="grow w-0 mt-0"
                      placeholder="Select a Template"
                      value={formattingTemplate}
                      onValueChange={(value) => {
                        if (value === "new") {
                          const newTemplate = aiMinutesTemplateRepo.init();
                          setSelectedTemplate(newTemplate);
                          setTemplateDialogOpen(true);
                        } else {
                          setFormattingTemplate(value);
                        }
                      }}
                      options={[
                        ...(aiMinutesTemplates?.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        )) ?? []),
                        <SelectItem key="new" value="new">
                          <PlusIcon size={20} className="inline-block mr-2" />
                          Create New Template
                        </SelectItem>,
                      ]}
                    />

                    <Button
                      disabled={!formattingTemplate || loading}
                      tooltip="Edit Template"
                      size="icon"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => {
                        const template = aiMinutesTemplates?.find(
                          (template) => template.id === formattingTemplate
                        );
                        if (!template) return;

                        setSelectedTemplate(template);
                        setTemplateDialogOpen(true);
                      }}
                    >
                      <PencilIcon size={18} />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p className="text-destructive text-lg mt-5 ml-3">{error}</p>
            )}

            {status && (
              <div className="flex flex-row items-center gap-4 mt-5 ml-3">
                <Spinner size="sm" />
                <p className="text-lg opacity-60">{status}</p>
              </div>
            )}
          </DialogBody>

          <DialogFooter className="justify-between">
            <Button
              disabled={loading}
              variant="destructive"
              onClick={() => setOpen?.(false)}
            >
              Cancel
            </Button>

            <Button disabled={loading} onClick={() => handleSubmit()}>
              <StarsIcon size={20} className="mr-2" /> Enhance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
